import React, { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import { Box, Skeleton, Typography, Stack, Chip, Tooltip } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { IResponsibleInfo } from 'services/SchoolPerimeterService/types';

interface CardPerimeterSchoolProps {
  name: string;
  widthProps: string;
  image?: string;
  date: string;
  titleTime: string;
  lastEventTime?: string;
  responsibles?: IResponsibleInfo[];
  responsibleName?: string;
  isOutdueEntry?: boolean;
  outdueEntryHours?: number;
  personType?: 'Student' | 'Responsible' | 'General';
}

const CardPersonSchool: React.FC<CardPerimeterSchoolProps> = ({
  name,
  image,
  date,
  titleTime,
  lastEventTime,
  responsibles,
  responsibleName,
  isOutdueEntry = false,
  outdueEntryHours,
  personType = 'Student',
}) => {
  const [imageToRender, setImageToRender] = useState<string>();

  useEffect(() => {
    if (image) {
      setImageToRender(image);
    }
  }, [image]);

  const renderResponsibles = () => {
    if (responsibles && responsibles.length > 0) {
      const headerLabel = responsibles.length > 1 ? 'Responsáveis' : 'Responsável';
      return (
        <Box>
          <Typography variant="body2" sx={{ color: '#b0bec5', fontWeight: 600, mb: 0.5 }}>
            {headerLabel}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {responsibles.map((resp) => {
              const chipSx = resp.status === 'inside'
                ? { color: '#66bb6a', borderColor: '#66bb6a', fontSize: '0.75rem' }
                : { color: '#90caf9', borderColor: '#90caf9', fontSize: '0.75rem' };

              const tooltipTitle = resp.status
                ? resp.status === 'inside'
                  ? 'Dentro'
                  : 'Fora'
                : '';

              return (
                <Tooltip key={resp.id} title={tooltipTitle}>
                  <Chip
                    label={resp.name}
                    size="small"
                    variant="outlined"
                    sx={chipSx}
                  />
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      );
    }
    if (responsibleName) {
      return (
        <Typography variant="body2" sx={{ color: '#b0bec5' }}>
          Responsável: {responsibleName}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" sx={{ color: '#b0bec5' }}>
        Responsável: Não informado
      </Typography>
    );
  };

  const getPersonTypeLabel = (type: string) => {
    switch (type) {
      case 'Student':
        return 'Aluno';
      case 'Responsible':
        return 'Responsável';
      case 'General':
        return 'Funcionário';
      default:
        return 'Outro';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Student':
        return '#2196F3'; // Azul
      case 'Responsible':
        return '#4CAF50'; // Verde
      case 'General':
        return '#FF9800'; // Laranja
      default:
        return '#90caf9';
    }
  };

  const borderColor = getTypeColor(personType);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Student':
        return <SchoolIcon sx={{ fontSize: 18, mr: 0.5 }} />;
      case 'Responsible':
        return <SupervisorAccountIcon sx={{ fontSize: 18, mr: 0.5 }} />;
      case 'General':
        return <BadgeIcon sx={{ fontSize: 18, mr: 0.5 }} />;
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        mb: 1,
        p: 2,
        backgroundColor: '#2e3b55',
        borderLeft: `4px solid ${borderColor}`,
        display: 'flex',
        gap: 2,
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#3a4a63',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Esquerda: Imagem + Informações */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minWidth: 0, alignItems: 'flex-start' }}>
        {/* Imagem */}
        <Box sx={{ flexShrink: 0, mt: 0 }}>
          {!imageToRender ? (
            <Skeleton variant="rounded" width={100} height={100} />
          ) : (
            <img
              width={100}
              height={100}
              src={imageToRender}
              alt={name}
              style={{
                borderRadius: 8,
                objectFit: 'cover',
              }}
            />
          )}
        </Box>

        {/* Tipo, Nome, Responsável */}
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, pt: 0 }}>
          {/* Tipo com ícone */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="caption"
              sx={{
                color: '#90caf9',
                fontWeight: 'bold',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {getTypeIcon(personType)}
              {getPersonTypeLabel(personType)}
            </Typography>
          </Box>

          {/* Nome */}
          <Typography variant="body1" sx={{ fontWeight: 500, color: '#fff' }}>
            {name}
          </Typography>

          {/* Responsável (apenas para alunos) */}
          {personType === 'Student' && (
            <Box>
              {renderResponsibles()}
            </Box>
          )}
        </Stack>
      </Box>

      {/* Direita: Horário */}
      <Stack spacing={0.5} sx={{ alignItems: 'flex-end', justifyContent: 'flex-start', flexShrink: 0, pt: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: '#90caf9',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }}
        >
          {titleTime}
        </Typography>
        <Tooltip
          title={
            lastEventTime
              ? new Date(lastEventTime).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })
              : 'Sem evento'
          }
        >
          <Typography variant="body2" sx={{ color: '#b0bec5', cursor: 'pointer' }}>
            {date}
          </Typography>
        </Tooltip>
        {isOutdueEntry && (
          <Tooltip
            title={
              outdueEntryHours
                ? `Último evento há ${outdueEntryHours} horas. Pode ser falso positivo (saída não registrada).`
                : 'Último evento >24h. Pode ser falso positivo (saída não registrada).'
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ffb74d' }} />
              <Typography variant="caption" sx={{ color: '#ffb74d', fontWeight: 600 }}>
                Sem atualização
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Card>
  );
};

export default CardPersonSchool;
